import { prefersReducedMotion } from '@theme/utilities';

/**
 * Web component for sections/guides-hub.liquid. Fully self-contained:
 * search-filters the topic cards, swaps which guide panel is visible (no
 * navigation, no re-fetch — every panel is already server-rendered, hidden
 * except the active one), and keeps the left step list in sync with the
 * right-hand accordion regardless of which side the user interacts with.
 */
class GuidesHub extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('[data-guides-search]');
    this.topics = this.querySelector('[data-guides-topics]');
    this.emptyState = this.querySelector('[data-guides-empty]');
    this.cards = Array.from(this.querySelectorAll('.guide-card'));
    this.panels = Array.from(this.querySelectorAll('.guide-panel'));

    this.activateFirstGuide();
    this.bindCardClicks();
    this.bindStepSync();
    this.bindSearch();
    this.setupReveal();
  }

  setupReveal() {
    const targets = Array.from(this.querySelectorAll('[data-reveal]'));
    if (!targets.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    targets.forEach((el) => io.observe(el));
  }

  scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  activateFirstGuide() {
    const firstCard = this.cards[0];
    if (firstCard) this.showGuide(firstCard, { scroll: false });
  }

  bindCardClicks() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.showGuide(card, { scroll: true }));
    });
  }

  showGuide(card, { scroll }) {
    const targetId = card.dataset.guideTarget;
    const panel = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;

    this.cards.forEach((c) => {
      const isActive = c === card;
      c.classList.toggle('is-active', isActive);
      c.setAttribute('aria-expanded', String(isActive));
    });

    this.panels.forEach((p) => {
      p.hidden = p !== panel;
    });

    if (panel && scroll) {
      panel.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
    }
  }

  bindStepSync() {
    this.panels.forEach((panel) => {
      const stepNavs = Array.from(panel.querySelectorAll('[data-step-nav]'));
      const details = Array.from(panel.querySelectorAll('[data-step-detail]'));

      stepNavs.forEach((nav) => {
        nav.addEventListener('click', () => {
          const targetId = nav.dataset.stepTarget;
          const targetDetail = targetId ? panel.querySelector(`#${CSS.escape(targetId)}`) : null;
          if (!targetDetail) return;

          details.forEach((d) => {
            if (d !== targetDetail) d.open = false;
          });
          targetDetail.open = true;

          targetDetail.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
        });
      });

      details.forEach((detail) => {
        detail.addEventListener('toggle', () => {
          if (!detail.open) return;

          // Keep the accordion exclusive: opening one closes the rest.
          details.forEach((d) => {
            if (d !== detail) d.open = false;
          });

          stepNavs.forEach((nav) => {
            nav.setAttribute('aria-current', String(nav.dataset.stepTarget === detail.id));
          });
        });
      });
    });
  }

  bindSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', () => {
      const query = this.searchInput.value.trim().toLowerCase();
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const haystack = (card.dataset.guideSearch || '').toLowerCase();
        const matches = query === '' || haystack.includes(query);
        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
    });
  }
}

if (!customElements.get('guides-hub')) {
  customElements.define('guides-hub', GuidesHub);
}
